import { useEffect, useState } from "react";
import axios from "axios";
import { KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";

const AUTH_API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;

const passwordChangeError = (error) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg)
      .filter((message) => typeof message === "string");
    return messages.join("; ") || "Password could not be updated.";
  }
  return typeof detail === "string" ? detail : "Password could not be updated.";
};

export default function PasswordChangePage() {
  const { clearSession, user } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [requiresSignIn, setRequiresSignIn] = useState(false);

  useEffect(() => {
    if (requiresSignIn && !user) {
      navigate("/login", { replace: true });
    }
  }, [navigate, requiresSignIn, user]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (newPassword !== confirmation) {
      const message = "The new password and confirmation do not match.";
      setError(message);
      toast.error(message);
      return;
    }
    setIsSaving(true);
    try {
      await axios.post(`${AUTH_API}/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      clearSession();
      setRequiresSignIn(true);
      toast.success("Your password has been updated. Sign in with your new password.");
    } catch (error) {
      const message = passwordChangeError(error);
      setError(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="password-change-page" data-testid="password-change-page">
      <section className="password-change-panel">
        <span className="operations-brand-mark" aria-hidden="true">
          S
        </span>
        <p className="operations-kicker">Secure your account</p>
        <h1>Choose your password</h1>
        <p>
          {user?.must_change_password
            ? `${user.name}, replace your temporary password with a personal password.`
            : `${user?.name}, enter your current password and choose a new password.`}
        </p>
        <form data-testid="password-change-form" onSubmit={submit}>
          <label htmlFor="current-password-input">
            {user?.must_change_password ? "Temporary password" : "Current password"}
            <input
              autoComplete="current-password"
              data-testid="current-password-input"
              id="current-password-input"
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
              type="password"
              value={currentPassword}
            />
          </label>
          <label htmlFor="new-password-input">
            New password
            <input
              autoComplete="new-password"
              data-testid="new-password-input"
              id="new-password-input"
              minLength={12}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              type="password"
              value={newPassword}
            />
          </label>
          <label htmlFor="confirm-password-input">
            Confirm new password
            <input
              autoComplete="new-password"
              data-testid="confirm-password-input"
              id="confirm-password-input"
              minLength={12}
              onChange={(event) => setConfirmation(event.target.value)}
              required
              type="password"
              value={confirmation}
            />
          </label>
          {error && (
            <p data-testid="password-change-error" role="alert">
              {error}
            </p>
          )}
          <button
            className="invoice-primary-button"
            data-testid="password-change-submit-button"
            disabled={isSaving}
            type="submit"
          >
            <KeyRound size={16} />
            {isSaving ? "Saving…" : "Save and continue"}
          </button>
        </form>
      </section>

    </main>
  );
}

